import React, { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

function BackupCodesDisplay({ codes, onDone }) {
  const handleCopy = () => {
    const text = codes.join("\n");
    navigator.clipboard.writeText(text);
  };

  const handleDownload = () => {
    const text = "Ouro 2FA Backup Codes\n" +
      "========================\n" +
      "Save these codes in a safe place.\n" +
      "Each code can only be used once.\n\n" +
      codes.join("\n");

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ouro-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Save these backup codes now. You will not be able to see them again.
      </Alert>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          fontFamily: "monospace",
          fontSize: "0.95rem",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          backgroundColor: "rgba(255,255,255,0.03)",
        }}
      >
        {codes.map((code, i) => (
          <Typography key={i} sx={{ fontFamily: "monospace", textAlign: "center" }}>
            {code}
          </Typography>
        ))}
      </Paper>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
        >
          Copy
        </Button>
        <Button variant="outlined" size="small" onClick={handleDownload}>
          Download
        </Button>
      </Box>
      <Button variant="contained" fullWidth onClick={onDone}>
        I have saved my backup codes
      </Button>
    </Box>
  );
}

export default function TwoFactorSetup({ user }) {
  const isEnabled = !!user?.twoFactorEnabled;

  const [step, setStep] = useState("idle");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [regenCode, setRegenCode] = useState("");

  const apiCall = useCallback(async (path, body) => {
    const res = await fetch(`${basePath}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || "Request failed");
    return data;
  }, []);

  const startSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall("/auth/2fa/setup", {});
      setQrCodeUrl(data.qrCodeUrl);
      setManualKey(data.manualEntryKey);
      setStep("scan");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async () => {
    if (!verifyCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall("/auth/2fa/verify-setup", { code: verifyCode.trim() });
      setBackupCodes(data.backupCodes);
      setStep("backup");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await apiCall("/auth/2fa/disable", { code: disableCode.trim() });
      setDisableDialogOpen(false);
      setDisableCode("");
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenCodes = async () => {
    if (!regenCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall("/auth/2fa/backup-codes", { code: regenCode.trim() });
      setBackupCodes(data.backupCodes);
      setRegenDialogOpen(false);
      setRegenCode("");
      setStep("backup");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupDone = () => {
    window.location.reload();
  };

  // Enabled state
  if (isEnabled && step === "idle") {
    return (
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <LockIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Two-Factor Authentication
          </Typography>
          <Chip
            icon={<CheckCircleIcon />}
            label="Enabled"
            color="success"
            size="small"
          />
        </Box>

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Your account is protected with two-factor authentication via an authenticator app.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setRegenDialogOpen(true)}
          >
            Regenerate Backup Codes
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => setDisableDialogOpen(true)}
          >
            Disable 2FA
          </Button>
        </Box>

        <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)}>
          <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter your current authenticator code to confirm.
            </Typography>
            <TextField
              fullWidth
              label="Authenticator code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 6 } }}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDisable}
              color="error"
              variant="contained"
              disabled={loading || disableCode.length < 6}
            >
              {loading ? <CircularProgress size={20} /> : "Disable"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={regenDialogOpen} onClose={() => setRegenDialogOpen(false)}>
          <DialogTitle>Regenerate Backup Codes</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This will invalidate your existing backup codes. Enter your current authenticator code to confirm.
            </Typography>
            <TextField
              fullWidth
              label="Authenticator code"
              value={regenCode}
              onChange={(e) => setRegenCode(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 6 } }}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRegenDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRegenCodes}
              variant="contained"
              disabled={loading || regenCode.length < 6}
            >
              {loading ? <CircularProgress size={20} /> : "Regenerate"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Showing backup codes after enable or regeneration
  if (step === "backup" && backupCodes) {
    return (
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <LockIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Backup Recovery Codes
          </Typography>
        </Box>
        <BackupCodesDisplay codes={backupCodes} onDone={handleSetupDone} />
      </Box>
    );
  }

  // Setup flow: scan QR step
  if (step === "scan") {
    return (
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <LockIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Set Up Two-Factor Authentication
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
        </Typography>

        {qrCodeUrl && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#fff",
              width: "fit-content",
              mx: "auto",
            }}
          >
            <img src={qrCodeUrl} alt="2FA QR Code" width={200} height={200} />
          </Box>
        )}

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Or enter this key manually:
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 3,
            fontFamily: "monospace",
            fontSize: "0.9rem",
            textAlign: "center",
            wordBreak: "break-all",
            backgroundColor: "rgba(255,255,255,0.03)",
          }}
        >
          {manualKey}
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" sx={{ mb: 1 }}>
          Enter the 6-digit code from your app to verify:
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            slotProps={{
              htmlInput: {
                maxLength: 6,
                style: { fontFamily: "monospace", fontSize: "1.2rem", letterSpacing: 4 },
              },
            }}
            variant="outlined"
            autoFocus
          />
          <Button
            variant="contained"
            onClick={confirmSetup}
            disabled={loading || verifyCode.length < 6}
          >
            {loading ? <CircularProgress size={20} /> : "Verify"}
          </Button>
        </Box>

        <Button size="small" onClick={() => setStep("idle")} color="inherit">
          Cancel
        </Button>
      </Box>
    );
  }

  // Default: not enabled, show enable button
  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <LockIcon sx={{ color: "text.secondary" }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Two-Factor Authentication
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Add an extra layer of security to your account. When enabled, you will need to enter a code from your authenticator app each time you sign in.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={startSetup}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={18} /> : <LockIcon />}
      >
        Enable Two-Factor Authentication
      </Button>
    </Box>
  );
}
